import { useSyncExternalStore } from 'react';

/**
 * Light, dark, or whatever the OS says. The choice is stamped on <html> as
 * `data-theme` (the CSS reads it, and so does the inline script in
 * index.html that paints the first frame). "system" removes the stamp and
 * lets the media query decide.
 */
export type Theme = 'light' | 'dark' | 'system';

const KEY = 'da.theme';
const listeners = new Set<() => void>();

function read(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

function apply(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') delete root.dataset.theme;
  else root.dataset.theme = theme;
}

export function setTheme(theme: Theme): void {
  try {
    if (theme === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, theme);
  } catch { /* storage blocked: the choice lasts this tab */ }
  apply(theme);
  for (const l of listeners) l();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const theme = useSyncExternalStore(subscribe, read, () => 'system' as Theme);
  return [theme, setTheme];
}

/** What is actually on screen right now — the resolved scheme, for charts and the like. */
export function useResolvedTheme(): 'light' | 'dark' {
  const [theme] = useTheme();
  const prefersDark = useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
    () => false,
  );
  return theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme;
}
